import type { IModule, Tracking } from "@ebay/experience-types-ebay";

const PAGE_ID = "4514735";
const MAP_MODULE_ID = "161587";
const EMPTY_MAP_MODULE_ID = "172878";

/**
 * Get all necessary component props for click tracking
 * @param {*} trackingList
 * @returns Object containing tracking attributes, which can be rendered on the component
 */
export function getPropsForClickTracking(trackingList?: Tracking[]): Record<string, string | undefined> {
  const sid = trackingList?.reduce<string | undefined>((sid, tracking) => sid || tracking.eventProperty?.sid, undefined);
  const legacyAttribute = sid ? { _sp: sid } : {};

  const clickTracking = trackingList?.find(({ actionKind }) => ["CLICK", "NAV"].includes(String(actionKind)));
  const trackingAttribute = clickTracking ? { "data-click": JSON.stringify(clickTracking) } : {};

  return { ...legacyAttribute, ...trackingAttribute };
}

/**
 * Get all necessary component props for view tracking
 * @param {*} trackingList
 * @returns Object containing data-view attribute, which can be rendered on the component
 */
export function getPropsForViewTracking(trackingList?: Tracking[]): Record<string, string> {
  return trackingList?.length ? { "data-view": JSON.stringify(trackingList) } : {};
}

/**
 * Get all necessary component props for empty map view tracking
 * @param {*} trackingList
 * @returns Object containing data-view attribute, which can be rendered on the component
 */
export function getPropsForEmptyMapViewTracking(trackingList: Tracking[] = []): Record<string, string> {
  const eventProperty = {
    mapview_entry_page_id: trackingList[0]?.eventProperty?.mapview_entry_page_id,
    moduledtl: `mi:${EMPTY_MAP_MODULE_ID}`,
  };
  const updatedTrackingList = [
    {
      ...trackingList[0],
      eventProperty,
    },
  ];
  return trackingList?.length ? { "data-view": JSON.stringify(updatedTrackingList) } : {};
}

/**
 * Get tracking data from module's metadata
 *
 * @param {} module - module object
 * @return trackingList from module metadata. Might be undefined
 */
export function getModuleMetaTracking(module?: IModule): Tracking[] | undefined {
  return module?.meta?.trackingList;
}

type EntityType = "button" | "marker" | "cluster" | "cluster_stacked";

/**
 *
 * @param element - HTML Element to add tracking attributes to
 * @param trackingList - List of tracking properties, may be undefined
 */
export function setAttrsForClickTracking(element: HTMLElement, trackingList?: Tracking[]): void {
  Object.entries(getPropsForClickTracking(trackingList))
    .filter(isTrackingValueDefined)
    .map(([key, value]) => {
      element.setAttribute(key, value!);
    });
}

const isTrackingValueDefined = (tracking: [string, string | undefined]): tracking is [string, string] => {
  const [, value] = tracking;
  return value !== undefined;
};

/**
 *
 * @param clickId - Clicked element tracking ID
 * @param entityType - Type of the clicked element (for logs)
 * @param entryPageId - Consumer page ID if used inside an iframe
 */
export function composeAttrsForClickTracking(clickId: string, entityType: EntityType, entryPageId?: string): Tracking {
  const eventProperty = {
    sid: `p${PAGE_ID}.m${MAP_MODULE_ID}.l${clickId}`,
    entity_type: entityType,
  };
  return {
    eventAction: "ACTN",
    eventFamily: "MAPVIEW",
    actionKind: "CLICK",
    eventProperty: entryPageId ? { ...eventProperty, mapview_entry_page_id: entryPageId } : eventProperty,
  };
}
